export interface IpLocation {
  city: string;
  state: string;
  zipCode: string;
  lat: number;
  lng: number;
}

const CACHE_KEY = 'fash_ip_location';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 4000;

/** Used when every lookup provider is unreachable or blocked. */
export const DEFAULT_LOCATION: IpLocation = {
  city: 'Soho',
  state: 'NY',
  zipCode: '10012',
  lat: 40.723301,
  lng: -74.002988,
};

type Provider = {
  url: string;
  parse: (json: any) => IpLocation | null;
};

// Keyless endpoints, tried in order. Ad blockers commonly block the first one,
// so the alternates matter more than they look.
const PROVIDERS: Provider[] = [
  {
    url: 'https://ipapi.co/json/',
    parse: (j) =>
      typeof j?.latitude === 'number' && typeof j?.longitude === 'number'
        ? {
            city: j.city || '',
            state: j.region_code || j.region || '',
            zipCode: j.postal || '',
            lat: j.latitude,
            lng: j.longitude,
          }
        : null,
  },
  {
    url: 'https://ipwho.is/',
    parse: (j) =>
      j?.success !== false && typeof j?.latitude === 'number' && typeof j?.longitude === 'number'
        ? {
            city: j.city || '',
            state: j.region_code || j.region || '',
            zipCode: j.postal || '',
            lat: j.latitude,
            lng: j.longitude,
          }
        : null,
  },
  {
    url: 'https://get.geojs.io/v1/ip/geo.json',
    parse: (j) => {
      const lat = parseFloat(j?.latitude);
      const lng = parseFloat(j?.longitude);
      return Number.isFinite(lat) && Number.isFinite(lng)
        ? {
            city: j.city || '',
            state: j.region || '',
            zipCode: '',
            lat,
            lng,
          }
        : null;
    },
  },
];

function readCache(): IpLocation | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { value, at } = JSON.parse(raw);
    if (Date.now() - at > CACHE_TTL_MS) return null;
    return value as IpLocation;
  } catch {
    return null;
  }
}

function writeCache(value: IpLocation) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ value, at: Date.now() }));
  } catch {
    // Storage unavailable (private mode) - lookup just repeats next visit.
  }
}

async function fetchJson(url: string): Promise<any | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** Resolve an approximate, city-level location from the visitor's IP address. */
export async function lookupIpLocation(): Promise<IpLocation> {
  const cached = readCache();
  if (cached) return cached;

  for (const provider of PROVIDERS) {
    const json = await fetchJson(provider.url);
    if (!json) continue;
    const parsed = provider.parse(json);
    if (parsed) {
      writeCache(parsed);
      return parsed;
    }
  }

  return DEFAULT_LOCATION;
}
